import { createSelector } from '@reduxjs/toolkit';
import pipe from 'lodash/flow';
import { AuthState } from '../../../types/AuthTypes';
import { isUnauthenticatedUser, isUnverifiedUser, isVerifiedUser } from './typeGuards';

interface AppState {
  authentication?: AuthState;
}

export const rootSelector = (state: AppState) => {
  if (!state.authentication) {
    throw new Error('Trying to read authentication state but module is not loaded');
  }
  return state.authentication;
};

export const getUser = createSelector(rootSelector, (state) => state.user);

export const getUserData = pipe(getUser, (user) => user.data);

export const getUserError = pipe(getUser, (user) => user.error);

export const hasUserError = pipe(getUserError, (error) => !!error);

export const isLoadingUser = pipe(getUserData, (data) => !data);

export const getUnverifiedUser = pipe(getUserData, (data) =>
  isUnverifiedUser(data) ? data : undefined,
);
export const getUnauthenticatedUser = pipe(getUserData, (data) =>
  isUnauthenticatedUser(data) ? data : undefined,
);
export const getVerifiedUser = pipe(getUserData, (data) =>
  isVerifiedUser(data) ? data : undefined,
);

export const getUnverifiedEmail = pipe(getUserData, (data) =>
  isUnverifiedUser(data) ? data.email : undefined,
);

export const userCategoriesSelector = pipe(getUserData, (data) =>
  isVerifiedUser(data) ? data.categories : undefined,
);

export const getImpacterTermsMetaData = createSelector(
  rootSelector,
  (state) => state.impacterTerms,
);

export const getImpacterProfile = createSelector(
  rootSelector,
  isLoadingUser,
  (state, isLoadingUser) => {
    return {
      ...state.impacter,
      fetching: isLoadingUser || state.impacter.fetching,
    };
  },
);

export const getNeedsToAcceptImpacterTerms = createSelector(
  getUserData,
  getImpacterTermsMetaData,
  (userData, impacterTerms) => {
    if (userData && impacterTerms.slug && impacterTerms.updated && 'acceptedTerms' in userData) {
      const updatedDate = new Date(impacterTerms.updated);
      const acceptedDateString = userData?.acceptedTerms?.find(
        ({ contentfulSlug }) => contentfulSlug === impacterTerms.slug,
      )?.acceptedAt;

      if (!acceptedDateString) {
        return true;
      }

      const acceptedDate = new Date(acceptedDateString);

      return updatedDate.getTime() - acceptedDate.getTime() > 0;
    } else {
      return false;
    }
  },
);
